import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from './Iconify';
import MenuPopover from './MenuPopover';
import Scrollbar from './Scrollbar';
import {
  fetchAdvertiseEditNotifications,
  markNotificationAsRead,
  markAllAsRead,
} from '../services/advertise-edit-notification';
import { AdvertiseEditNotification } from '../services/advertise-edit-notification/types';
import { useAppSelector } from '../store';
import { encryptString } from '../utils/url';

interface NotificationDropdownProps {
  unreadCount: number;
  onCountChange?: (count: number) => void;
}

const NotificationDropdown = ({
  unreadCount,
  onCountChange,
}: NotificationDropdownProps) => {
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AdvertiseEditNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const userRole = useAppSelector((state) => state.auth.user.userRole);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await fetchAdvertiseEditNotifications({
        limit: 10,
        skip: 0,
      });
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNotificationClick = async (notification: AdvertiseEditNotification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      try {
        await markNotificationAsRead(notification.id);
        // Update local state
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, isRead: true } : n
          )
        );
        // Update unread count
        if (onCountChange) {
          onCountChange(Math.max(0, unreadCount - 1));
        }
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate based on notification type
    if (notification.renewalRequestId) {
      // Navigate to license renewal page for admins
      if (userRole === 'admin' || userRole === 'adminTier2') {
        navigate('/users');
      } else {
        navigate('/license-renewal');
      }
    } else if (notification.advertiseId) {
      // Navigate to ad detail page
      const encryptedId = encryptString(notification.advertiseId);
      if (userRole === 'company') {
        navigate(`/company-advertise/view/${encryptedId}`);
      } else if (userRole === 'admin' || userRole === 'adminTier2') {
        navigate(`/advertise/view/${encryptedId}`);
      }
    }

    handleClose();
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      // Update local state
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true }))
      );
      // Update unread count
      if (onCountChange) {
        onCountChange(0);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 1,
          width: 40,
          height: 40,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
            },
          }),
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Iconify icon="eva:bell-fill" width={24} height={24} />
        </Badge>
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 360 }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllAsRead}>
              Mark all as read
            </Button>
          )}
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <Scrollbar sx={{ maxHeight: 400 }}>
            <List sx={{ p: 0 }}>
              {notifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  disablePadding
                  sx={{
                    bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                    '&:hover': {
                      bgcolor: 'action.selected',
                    },
                  }}
                >
                  <ListItemButton onClick={() => handleNotificationClick(notification)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: notification.isRead ? 'normal' : 'bold',
                            }}
                          >
                            {(() => {
                              if (notification.renewalRequestId) {
                                const status = notification.renewalRequest?.status || 'pending';
                                if (status === 'approved') {
                                  return 'License Renewal Approved';
                                } else if (status === 'rejected') {
                                  return 'License Renewal Rejected';
                                }
                                return 'License Renewal Request';
                              }
                              return 'Ad Edited';
                            })()}
                          </Typography>
                          {!notification.isRead && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'primary.main',
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          {notification.renewalRequestId ? (
                            <>
                              <Typography variant="body2" color="text.secondary">
                                {(() => {
                                  const status = notification.renewalRequest?.status || 'pending';
                                  const adminName = notification.editedByUser?.name || 'Admin';
                                  const companyName = notification.renewalRequest?.company?.name || 'Company';
                                  
                                  // Different messages for admin vs company
                                  if (userRole === 'admin' || userRole === 'adminTier2') {
                                    // Admin view messages
                                    if (status === 'approved') {
                                      return `${companyName}'s license renewal request has been approved`;
                                    } else if (status === 'rejected') {
                                      return `${companyName}'s license renewal request has been rejected`;
                                    } else {
                                      return `${companyName} submitted a license renewal request`;
                                    }
                                  } else {
                                    // Company view messages
                                    if (status === 'approved') {
                                      return `Your license renewal request has been approved by ${adminName}`;
                                    } else if (status === 'rejected') {
                                      return `Your license renewal request has been rejected by ${adminName}`;
                                    } else {
                                      return 'Your license renewal request is pending review';
                                    }
                                  }
                                })()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Status: {notification.renewalRequest?.status || 'Pending'}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2" color="text.secondary">
                                {notification.advertise?.title || 'Advertisement'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(() => {
                                  const editorName = notification.editedByUser?.name || 'Someone';
                                  const editorRole = notification.editedByUser?.userRole;
                                  if (editorRole === 'agent') {
                                    return `Agent ${editorName} edited this ad`;
                                  } else if (editorRole === 'company') {
                                    return `Company Admin ${editorName} edited this ad`;
                                  }
                                  return `${editorName} edited this ad`;
                                })()}
                              </Typography>
                            </>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {formatDate(notification.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Scrollbar>
        )}

        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button size="small" fullWidth onClick={handleClose}>
                Close
              </Button>
            </Box>
          </>
        )}
      </MenuPopover>
    </>
  );
};

export default NotificationDropdown;

